import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Box,
  Chip,
  IconButton,
} from '@mui/material';
import {
  AccountBalance,
  AccountCircle,
  AccountTree,
  Apartment,
  Approval,
  Badge,
  BookmarkBorder,
  Business,
  CalendarMonth,
  Category,
  Close,
  ContentCopy,
  CurrencyExchange,
  EventNote,
  FileDownload,
  FolderSpecial,
  GitHub,
  Groups,
  Hub,
  InfoOutlined,
  LocalOffer,
  Paid,
  Percent,
  Receipt,
  Rule,
  Search,
  Security,
  Settings,
  Straighten,
  SwapHoriz,
  ViewColumn,
  Wallet,
  Warehouse,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useStore } from '@viilutalous/store';

const MODULES = ['company', 'accounting', 'invoicing', 'purchasing', 'maventa', 'inventory', 'crm', 'hr', 'salaries', 'projects', 'github_integration', 'metered_billing'];

const settingsItems = [
  { key: 'user_settings', icon: <AccountCircle />, path: '/app/settings', tKey: 'nav.user_settings', module: 'company' },
  { key: 'company_info', icon: <Business />, path: '/app/company-settings', module: 'company' },
  { key: 'bank_accounts', icon: <AccountBalance />, path: '/app/company-settings?tab=bank_details', tKey: 'company_settings.bank_details_tab', module: 'company' },
  { key: 'einvoice', icon: <Receipt />, path: '/app/company-settings?tab=einvoice', tKey: 'company_settings.einvoice_tab', module: 'invoicing' },
  { key: 'accounting_settings', icon: <Settings />, path: '/app/company-settings?tab=accounting', tKey: 'company_settings.accounting_tab', module: 'accounting' },
  { key: 'vat_configuration', icon: <Percent />, path: '/app/vat-configuration', module: 'accounting' },
  { key: 'coa_configuration', icon: <AccountTree />, path: '/app/coa-configuration', module: 'accounting' },
  { key: 'coa_role_configs_configuration', icon: <Rule />, path: '/app/coa-role-configs-configuration', module: 'accounting' },
  { key: 'coa_groups_configuration', icon: <FolderSpecial />, path: '/app/coa-groups-configuration', module: 'accounting' },
  { key: 'financial_years_configuration', icon: <CalendarMonth />, path: '/app/financial-years-configuration', module: 'accounting' },
  { key: 'voucher_types_configuration', icon: <BookmarkBorder />, path: '/app/voucher-types-configuration', module: 'accounting' },
  { key: 'entry_types_configuration', icon: <Category />, path: '/app/entry-types-configuration', module: 'accounting' },
  { key: 'dimensions_configuration', icon: <Hub />, path: '/app/dimensions-configuration', module: 'accounting' },
  { key: 'charge_financing_balances', icon: <AccountBalance />, path: '/app/charge-financing-balances', tKey: 'nav.charge_financing_balances', module: 'accounting' },
  { key: 'currencies_configuration', icon: <CurrencyExchange />, path: '/app/currencies-configuration', module: 'accounting' },
  { key: 'exchange_rates', icon: <CurrencyExchange />, path: '/app/exchange-rates', module: 'accounting' },
  { key: 'units_configuration', icon: <Straighten />, path: '/app/units-configuration', module: 'inventory' },
  { key: 'stocks', icon: <Warehouse />, path: '/app/stocks', tKey: 'nav.stocks', module: 'inventory' },
  { key: 'product_groups', icon: <Category />, path: '/app/product-groups', tKey: 'nav.product_groups', module: 'inventory' },
  { key: 'contact_groups', icon: <Groups />, path: '/app/contact-groups', tKey: 'nav.contact_groups', module: 'crm' },
  { key: 'metered_units', icon: <Apartment />, path: '/app/metered-billing/subjects', tKey: 'metered_billing.subjects', module: 'metered_billing' },
  { key: 'payment_terms_configuration', icon: <EventNote />, path: '/app/payment-terms-configuration', module: 'invoicing' },
  { key: 'payment_methods_configuration', icon: <Wallet />, path: '/app/payment-methods-configuration', module: 'invoicing' },
  { key: 'transfer_invoices', icon: <SwapHoriz />, path: '/app/transfer-invoices', tKey: 'nav.transfer_invoices', module: 'invoicing' },
  { key: 'maventa_settings', icon: <Settings />, path: '/app/settings/maventa', tKey: 'nav.maventa_settings', module: 'maventa' },
  { key: 'github_integration', icon: <GitHub />, path: '/app/settings/webhooks', tKey: 'webhooks.github.title', module: 'github_integration' },
  { key: 'si_messages', icon: <InfoOutlined />, path: '/app/settings/si-messages', tKey: 'nav.si_messages', module: 'maventa' },
  { key: 'ri_messages', icon: <InfoOutlined />, path: '/app/settings/ri-messages', tKey: 'nav.ri_messages', module: 'maventa' },
  { key: 'employees', icon: <Badge />, path: '/app/employees', module: 'hr' },
  { key: 'departments', icon: <AccountTree />, path: '/app/departments', module: 'hr' },
  { key: 'expense_categories_configuration', icon: <Receipt />, path: '/app/expense-categories', module: 'hr' },
  { key: 'tenant_users', icon: <Groups />, path: '/app/tenant-users', tKey: 'tenant_users.title', module: 'company' },
  { key: 'role_management', icon: <Security />, path: '/app/role-management', tKey: 'roles.title', module: 'company' },
  { key: 'export_data', icon: <FileDownload />, path: '/app/export-data', module: 'company' },
  { key: 'salaries_settings', icon: <Paid />, path: '/app/salaries/settings', tKey: 'nav.salaries_settings', module: 'salaries' },
  { key: 'stages_matrix', icon: <ViewColumn />, path: '/app/projects/stages-matrix', tKey: 'nav.stages_matrix', module: 'projects' },
  { key: 'project_tags', icon: <LocalOffer />, path: '/app/projects/tags', tKey: 'nav.tags', module: 'projects' },
  { key: 'project_templates', icon: <ContentCopy />, path: '/app/project-templates', tKey: 'nav.project_templates', module: 'projects' },
  { key: 'user_groups', icon: <Approval />, path: '/app/user-groups', tKey: 'settings.user_groups', module: 'company' },
  { key: 'purchase_invoice_approval_rules', icon: <Approval />, path: '/app/purchasing/approval-rules', tKey: 'settings.purchase_invoice_approval_rules', module: 'purchasing' },
];

export default function SettingsFinderDialog({ open, onClose, onNavigate }) {
  const { t } = useTranslation();
  const { enabledModules } = useStore();
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState(null);

  const handleClose = () => {
    setSearch('');
    setModuleFilter(null);
    onClose();
  };

  const handleItemClick = (path) => {
    setSearch('');
    setModuleFilter(null);
    onNavigate(path);
    onClose();
  };

  const getLabel = (item) => t(item.tKey || `nav.${item.key}`);

  const filtered = settingsItems.filter((item) => {
    if (enabledModules && item.module !== 'company' && !enabledModules.has(item.module)) return false;
    if (moduleFilter && item.module !== moduleFilter) return false;
    const query = search.toLowerCase();
    const label = getLabel(item).toLowerCase();
    const desc = t(`settings.desc.${item.key}`).toLowerCase();
    return label.includes(query) || desc.includes(query);
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      TransitionProps={{
        onEntered: () => {
          document.getElementById('settings-search-input')?.focus();
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {t('nav.settings')}
        <IconButton size="small" onClick={handleClose} edge="end">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <TextField
          id="settings-search-input"
          fullWidth
          size="small"
          placeholder={t('settings.search_placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {MODULES.filter((mod) => mod === 'company' || !enabledModules || enabledModules.has(mod)).map((mod) => (
            <Chip
              key={mod}
              label={t(`settings.module.${mod}`)}
              size="small"
              variant={moduleFilter === mod ? 'filled' : 'outlined'}
              color={moduleFilter === mod ? 'primary' : 'default'}
              onClick={() => setModuleFilter(moduleFilter === mod ? null : mod)}
            />
          ))}
        </Box>
        <List dense>
          {filtered.map((item) => (
            <ListItemButton key={item.key} onClick={() => handleItemClick(item.path)}>
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={getLabel(item)} secondary={t(`settings.desc.${item.key}`)} />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
}
